import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import {
  CannedReplyForm,
  type CannedReplyFormValues,
} from "@/components/admin/support/canned-reply-form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MessageSquareText,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import type { CannedReply } from "@db/schema";

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent ?? "";
}

export default function CannedRepliesPage() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReply, setEditingReply] = useState<CannedReply | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CannedReply | null>(null);

  const { data: replies, isLoading } = useQuery<CannedReply[]>({
    queryKey: ["/api/support/canned-replies"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const createMutation = useMutation({
    mutationFn: async (data: CannedReplyFormValues) => {
      const res = await apiRequest("POST", "/api/support/canned-replies", data);
      return (await res.json()) as CannedReply;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/canned-replies"],
      });
      setDialogOpen(false);
      toast({ title: "Canned reply created" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to create canned reply",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: CannedReplyFormValues;
    }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/support/canned-replies/${id}`,
        data,
      );
      return (await res.json()) as CannedReply;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/canned-replies"],
      });
      setDialogOpen(false);
      setEditingReply(null);
      toast({ title: "Canned reply updated" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update canned reply",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/support/canned-replies/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/canned-replies"],
      });
      setDeleteTarget(null);
      toast({ title: "Canned reply deleted" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to delete canned reply",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function handleOpenCreate() {
    setEditingReply(null);
    setDialogOpen(true);
  }

  function handleOpenEdit(reply: CannedReply) {
    setEditingReply(reply);
    setDialogOpen(true);
  }

  function handleFormSubmit(data: CannedReplyFormValues) {
    if (editingReply) {
      updateMutation.mutate({ id: editingReply.id, data });
    } else {
      createMutation.mutate(data);
    }
  }

  const columns: ColumnDef<CannedReply>[] = [
    {
      key: "title",
      header: "Title",
      cell: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      key: "scope",
      header: "Scope",
      cell: (row) => (
        <Badge variant="outline">
          {row.isShared ? "Shared" : "Personal"}
        </Badge>
      ),
      className: "w-[100px]",
    },
    {
      key: "preview",
      header: "Preview",
      cell: (row) => {
        const text = row.bodyHtml ? stripHtml(row.bodyHtml) : "";
        return (
          <span className="text-sm text-muted-foreground line-clamp-1">
            {text || "\u2014"}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEdit(row);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      className: "w-[50px]",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Canned Replies"
        description="Manage reusable reply templates"
        action={
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Reply
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={replies}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        emptyIcon={MessageSquareText}
        emptyTitle="No canned replies"
        emptyDescription="Create reusable reply templates to speed up responses."
        emptyAction={
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Reply
          </Button>
        }
      />

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditingReply(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingReply ? "Edit Canned Reply" : "Add Canned Reply"}
            </DialogTitle>
            <DialogDescription>
              {editingReply
                ? "Update the reply template below."
                : "Create a new reusable reply template."}
            </DialogDescription>
          </DialogHeader>
          <CannedReplyForm
            key={editingReply?.id ?? "new"}
            defaultValues={
              editingReply
                ? {
                    title: editingReply.title,
                    bodyHtml: editingReply.bodyHtml ?? "",
                    isShared: editingReply.isShared,
                  }
                : undefined
            }
            onSubmit={handleFormSubmit}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Canned Reply"
        description={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() =>
          deleteTarget && deleteMutation.mutate(deleteTarget.id)
        }
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
